import React, { FC, useEffect, useMemo } from 'react';

import {
  FormControl,
  FormHelperText,
  FormErrorMessage,
  Skeleton,
  Box,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';

import { Select } from 'app/components/DataEntry/Select';
import { CustomTooltip } from 'components/DataDisplay';
import { FormLabel } from 'components/DataEntry/Form/fields/FormLabel';

import { useErrorMessage } from '../index';
import { useStyles } from '../index';
import {
  FieldProps,
  ReactSelectFieldSchema,
  ReactSelectFieldStyles,
} from '../types';

export const ReactSelectField: FC<FieldProps<ReactSelectFieldSchema>> = ({
  id,
  name,
  field,
  isLoading,
}) => {
  const {
    label,
    helperText,
    isRequired,
    shouldDisplay,
    helpIcon,
    onChange,
    isMulti,
    defaultValue,
    styles = {},
    value,
    helpTooltip,
    onClickHelpIcon,
    helpLabel,
    error,
    tooltip,
    errorTooltipStyles,
    ...rest
  } = field;
  const { register, watch, setValue, getValues } = useFormContext();
  const values = watch();
  const fieldStyles = useStyles<ReactSelectFieldStyles>(
    'reactSelectField',
    styles,
  );

  /* useEffect(() => {
    register(name);
  }, [register, name]);*/

  useEffect(() => {
    setValue(name, value ?? defaultValue ?? (isMulti ? [] : null));
  }, [value, defaultValue, name, setValue, isMulti]);

  const selectValue =
    getValues(name) ?? value ?? defaultValue ?? (isMulti ? [] : null);

  const errorMessage = useErrorMessage(name, label);

  const isVisible = useMemo(() => {
    return shouldDisplay ? shouldDisplay(values) : true;
  }, [values, shouldDisplay]);

  const renderSelectField = () => (
    <Box {...fieldStyles.root}>
      <Select
        data-test={id}
        styles={fieldStyles.select}
        value={selectValue}
        {...register(name)}
        // defaultValue={defaultValue}
        onChange={selected => {
          onChange?.(selected);
          setValue(name, selected);
        }}
        isLoading={isLoading}
        isMulti={isMulti}
        menuPortalTarget={document.body}
        showClearIndicator={false}
        {...rest}
      />
    </Box>
  );

  return isVisible ? (
    <FormControl
      key={`${name}-control`}
      isRequired={isRequired}
      isInvalid={!!errorMessage}
      {...fieldStyles.control}
    >
      {!!label && (
        <FormLabel
          htmlFor={name}
          label={label}
          helpIcon={helpIcon}
          styles={fieldStyles.label}
          helpTooltip={helpTooltip}
          helpLabel={helpLabel}
          isRequired={isRequired}
          onClickHelpIcon={onClickHelpIcon}
        />
      )}
      <Skeleton isLoaded={!isLoading}>
        {errorMessage || error || tooltip ? (
          <CustomTooltip
            label={error ?? errorMessage ?? tooltip}
            bg="white"
            border="1px solid"
            borderColor={!!tooltip ? 'primary' : 'critical'}
            color={!!tooltip ? 'primary' : 'critical'}
            width="full"
            hasArrow={false}
            {...errorTooltipStyles}
          >
            {renderSelectField()}
          </CustomTooltip>
        ) : (
          renderSelectField()
        )}
      </Skeleton>
      {!!helperText && (
        <FormHelperText {...fieldStyles.helperText}>
          {helperText}
        </FormHelperText>
      )}
      {!!errorMessage && (
        <FormErrorMessage {...fieldStyles.errorMessage}>
          {errorMessage}
        </FormErrorMessage>
      )}
    </FormControl>
  ) : null;
};
